import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Spin, Alert, Button, Grid } from "antd";
import "./calendario.css";
// IMPORTANTE: en antd v5 + React 19 los métodos estáticos importados
// directamente desde "antd" (Modal.confirm, message.error) NO funcionan —
// crean un root huérfano y el modal/toast nunca se renderiza, por lo que
// el `onOk` jamás se dispara. Hay que usar las instancias capturadas vía
// App.useApp() y expuestas en utils/antdStatic. Ver antdStatic.ts §1-§6.
// Esto explica el reporte "el botón eliminar no hace nada" — el Modal.confirm
// se llamaba pero no rendereaba, por eso el delete nunca corría.
import { getMsg, getModal } from "../../utils/antdStatic";
import { copiarInstruccionesDePropiedad } from "../../utils/mensajeInicial";
import { apiFetch } from "../../services/apiClient";
import {
  useCalendarioLogic,
  CalendarCellData,
  type CalendarRow,
  type CellThirdKey,
  type CellState,
  type CalendarEventRef,
  type Property,
} from "./useCalendarioLogic";
import CalendarioGrid from "./calendarioGrid";
import CalendarioControles from "./CalendarioControles";
import Layout from "../../Components/Layout";
import { useAuth } from "../../context/AuthContext";
import { useCalendarColors } from "./useCalendarColors";
import { isAdminRole } from "../../utils/roles.utils";
import { formatPlataforma } from "../../utils/plataforma.utils";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { formatPhoneDisplay } from "../../utils/formatPhone";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { mercadoApi, MercadoCalendarioResumenItem } from "../../services/mercado.api";
import { getPaleta, PALETAS } from "../../utils/mercadoPaletas";
import CalendarioClienteMonth from "./calendarioClienteMonth";
import BookingDetailModal from "./bookingDetailModal";
import CalendarCreateModal, {
  type CreatePayload,
  type RangeSelection,
} from "./calendarCreateModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { seedEquipoAseo } from "../../utils/aseos.utils";
import "dayjs/locale/es";
import MetaEditModal, { type MetaField } from "./MetaEditModal";
import { ahoraChile } from "../../utils/fechaChile";
import { useHoyChile } from "../../hooks/useHoyChile";
import { useMercadoChanged } from "../../hooks/useMercadoChanged";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import {
  acotarRangoCalendario,
  MENSAJE_RANGO_CALENDARIO_RECORTADO,
  RangoCalendarioISO,
} from "../../utils/rangoCalendario";

// Locale español para el nombre del mes del atajo "Mes completo" ("julio 2026").
// Va DESPUÉS de todos los imports (regla import/first del ESLint de CRA).
dayjs.locale("es");

// Paleta por defecto DISTINTA a la de Mercado (gris→verde) para diferenciarlas.
const MERCADO_PALETA_DEFAULT = "azul-mar";

const ISO_FMT = "YYYY-MM-DD";

const addDays = (d: Dayjs, days: number) => d.add(days, "day");

// Mapea una hora de INICIO "HH:mm" a su tercio del día (borde inferior
// inclusivo, strict `<`): mañana <11:00, mediodía <15:00, tarde/noche el
// resto. Un inicio a las 15:00 es evening; a las 11:00 es mid. SOLO para
// horas de inicio — para horas de TÉRMINO usar tercioFinDeHHmm. Round 2026-07-03.
const tercioDeHHmm = (hhmm?: string | null): "morning" | "mid" | "evening" => {
  if (!hhmm) return "morning";
  const [h, m] = hhmm.slice(0, 5).split(":").map(Number);
  const min = (h || 0) * 60 + (m || 0);
  if (min < 11 * 60) return "morning";
  if (min < 15 * 60) return "mid";
  return "evening";
};

// Igual que tercioDeHHmm pero para horas de TÉRMINO. El instante de fin es el
// borde SUPERIOR half-open del tercio: "11:00" = fin de mañana (el minuto
// 11:00 ya no está ocupado), "15:00" = fin de mediodía, "24:00"/"00:00" = fin
// de noche. Espeja `normalizeEndTimeForTercio` del backend
// (stays.controller.js): sin esto, un bloqueo por precio $0 (hora_termino
// "11:00") abría en edición con tercio "mediodía"/14:59 y al guardar extendía
// el bloqueo un tercio de más. Los bordes de tercio se mapean explícitamente;
// las horas internas (10:59, 14:59, salida tardía 14:30…) caen a la lógica de
// inicio, que es correcta para ellas. Round 2026-07-03.
const tercioFinDeHHmm = (hhmm?: string | null): "morning" | "mid" | "evening" => {
  if (!hhmm) return "morning";
  const s = hhmm.slice(0, 5);
  if (s === "11:00") return "morning";
  if (s === "15:00") return "mid";
  if (s === "24:00" || s === "00:00") return "evening";
  return tercioDeHHmm(s);
};

// Dispatcher por rol. CRÍTICO para el rendimiento del cliente: la bifurcación
// va ARRIBA del hook de datos. Antes, `Calendario` llamaba useCalendarioLogic()
// en su cuerpo y recién ~950 líneas después hacía `return <CalendarioClienteMonth/>`
// para el cliente; como los early-returns de React NO deshacen hooks ya
// invocados, el cliente terminaba corriendo DOS instancias del hook (la del
// padre desperdiciada + la del hijo) → 2× /propiedades + 2× /stays/calendario
// (¡2× el round-trip lento a Stays!) + 2× el pipeline de memos. Con el
// dispatcher, cada rol monta UNA sola instancia del hook. Ver
// docs/modulo-calendario.md y el diagnóstico de rendimiento (round 2026-07-16).
export default function Calendario() {
  const { user } = useAuth();
  if (!isAdminRole(user?.rol)) {
    return <CalendarioClienteMonth />;
  }
  return <CalendarioAdmin />;
}

function CalendarioAdmin() {
  const {
    loading,
    refreshing,
    error,
    properties,
    propSearch,
    setPropSearch,
    setPropSearchSinRecordar,
    guestSearch,
    setGuestSearch,
    setGuestSearchSinRecordar,
    propSort,
    setPropSort,
    propFilters,
    setPropFilters,
    setPropFiltersSinRecordar,
    propFilterOptions,
    dates,
    getCell,
    sidebarOpen,
    toggleSidebar,
    calendarRows,
    filteredAseadoras,
    loadingAseadoras,
    refetch,
    fetchEventDetail,
    range,
    setRange: setRangeSinTope,
    setRangeSinRecordar: setRangeDeEnlaceSinTope,
    staysCalendarData,
    fetchPropertyMeta,
    refreshPropertyMeta,
    refreshCalendar,
  } = useCalendarioLogic();
  // Todo cambio de rango de esta página pasa por el tope de 14 meses (round
  // 2026-09-11). Los selectores rechazan antes y avisan; hasta acá llegan además
  // los caminos que arman el rango con las fechas de un registro —deep-links de
  // /arriendos, Home y Alertas, y el buscador por código—, que no validaban
  // nada: un registro más largo que el tope se recorta a sus primeros 14 meses.
  const acotarYAvisar = useCallback((rango: RangoCalendarioISO) => {
    const acotado = acotarRangoCalendario(rango);
    if (acotado.recortado) {
      // Con key, el aviso se reemplaza en vez de apilarse: en desarrollo
      // (StrictMode) el efecto del deep-link corre dos veces.
      getMsg()?.warning({
        key: "rango-calendario-recortado",
        content: MENSAJE_RANGO_CALENDARIO_RECORTADO,
        duration: 6,
      });
    }
    return acotado.rango;
  }, []);
  const setRange = useCallback(
    (rango: RangoCalendarioISO) => {
      setRangeSinTope(acotarYAvisar(rango));
    },
    [acotarYAvisar, setRangeSinTope],
  );
  // El rango que impone un enlace directo (`?from=&to=` de Inicio, Alertas,
  // /arriendos y /finanzas-empresa) NO es una preferencia: el admin no lo
  // eligió. Se aplica a la vista pero no se guarda; si se guardara, la visita
  // siguiente sin parámetros abriría en las fechas de esa alerta en vez de en
  // el default relativo a hoy.
  const aplicarRangoDeEnlace = useCallback(
    (rango: RangoCalendarioISO) => {
      setRangeDeEnlaceSinTope(acotarYAvisar(rango));
    },
    [acotarYAvisar, setRangeDeEnlaceSinTope],
  );
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Carga los colores universales del calendario desde backend (categoria=
  // calendar_colors) y los inyecta como CSS vars en :root. Garantiza que
  // todos los usuarios (admin y cliente) vean el mismo color por evento.
  // Ver useCalendarColors.ts y docs/modulo-calendario.md.
  useCalendarColors();

  // Mapa id→{geo, cliente} para el subtexto de cada fila en la grilla. Se arma
  // desde `properties` (lista completa), no desde las filas filtradas.
  const propertyInfoById = useMemo(() => {
    const m: Record<
      number,
      {
        regionNombre?: string | null;
        comunaNombre?: string | null;
        sectorNombre?: string | null;
        clienteNombre?: string | null;
      }
    > = {};
    for (const p of properties) {
      m[p.id] = {
        regionNombre: p.regionNombre,
        comunaNombre: p.comunaNombre,
        sectorNombre: p.sectorNombre,
        clienteNombre: p.clienteNombre,
      };
    }
    return m;
  }, [properties]);

  // ── Fila informativa de MERCADO (admin, round 2026-07-24) ──────────────────
  // Switch on/off + paleta PROPIA del calendario (distinta a la de Mercado),
  // ambos recordados por usuario en este navegador (round 2026-09-24: antes se
  // guardaban con una clave global del navegador, así que en un equipo
  // compartido un admin heredaba la elección del otro). Los datos (ocupación +
  // banda por propiedad para el rango) se traen batcheados en un solo request
  // por rango.
  const [mercadoOn, setMercadoOn] = useFiltroPersistido<boolean>(
    "calendario.mercado",
    false,
    { validar: (v) => typeof v === "boolean" },
  );
  const [mercadoPaletaId, setMercadoPaletaId] = useFiltroPersistido<string>(
    "calendario.mercadoPaleta",
    MERCADO_PALETA_DEFAULT,
    { validar: (v) => PALETAS.some((p) => p.id === v) },
  );
  const [mercadoByProperty, setMercadoByProperty] = useState<
    Record<number, MercadoCalendarioResumenItem>
  >({});
  const mercadoPaleta = useMemo(() => getPaleta(mercadoPaletaId), [mercadoPaletaId]);

  // Fetch del resumen de mercado: SOLO cuando el switch está encendido. Depende
  // del rango (no de filtros/orden): trae TODAS las propiedades con código de
  // una vez, así el mapa cubre cualquier fila visible. Se re-consulta al cambiar
  // de rango, con el botón «Refrescar» y cuando llegan datos nuevos del scraper
  // (`mercadoToken`, round 2026-09-11: antes la fila se quedaba con la ocupación
  // y la banda viejas hasta recargar la página). El mapa anterior se mantiene
  // hasta que llega el nuevo, sin parpadeo. `silent` en la api → un fallo no
  // molesta con toasts.
  const [mercadoToken, setMercadoToken] = useState(0);
  useMercadoChanged((c) => {
    if (mercadoOn && (c.filas || c.mapeo || c.config || c.borrado)) {
      setMercadoToken((k) => k + 1);
    }
  });
  useEffect(() => {
    if (!mercadoOn) {
      setMercadoByProperty({});
      return;
    }
    let cancelado = false;
    (async () => {
      const res = await mercadoApi.getCalendarioResumen(range.from, range.to);
      if (cancelado || !res?.propiedades) return;
      const map: Record<number, MercadoCalendarioResumenItem> = {};
      for (const p of res.propiedades) map[p.propiedadId] = p;
      setMercadoByProperty(map);
    })();
    return () => {
      cancelado = true;
    };
  }, [mercadoOn, range.from, range.to, mercadoToken]);

  // Apply date range from query params (e.g. from Inicio "Ver en calendario")
  const [scrollToPropertyId, setScrollToPropertyId] = useState<string | null>(null);
  // Buscador por Código de estadía AF (#N) del calendario (admin). Al buscar,
  // saltamos al arriendo: movemos el rango a sus fechas, hacemos scroll a su
  // fila y resaltamos su barra. `highlightBookingId` gatilla el flash de barra.
  const [codeSearch, setCodeSearch] = useState("");
  const [codeSearching, setCodeSearching] = useState(false);
  const [highlightBookingId, setHighlightBookingId] = useState<number | null>(
    null,
  );
  // Deep-link de edición: `/calendario?editBooking=<id>&propertyId=<p>&from=<ing>&to=<sal>`
  // abre automáticamente el modal de edición de ESE arriendo (lo usa el enlace
  // por fila de /arriendos). Guardamos TODO el payload acá porque el effect de
  // params limpia la query (setSearchParams({}) abajo) — hay que capturarlo
  // antes. Con id+propiedad+fechas el editor abre sin depender de encontrar el
  // booking en la data del calendario (evita carreras).
  const [pendingEdit, setPendingEdit] = useState<{
    id: number;
    propertyId: number;
    ingreso: string;
    salida: string;
  } | null>(null);
  // Guard one-shot: onEditEvent del deep-link se dispara EXACTAMENTE una vez
  // (si el effect re-corre por refrescos de datos, no re-abre ni apila toasts).
  const editFiredRef = useRef(false);
  useEffect(() => {
    const qFrom = searchParams.get("from");
    const qTo = searchParams.get("to");
    const qPropertyId = searchParams.get("propertyId");
    const qEditBooking = searchParams.get("editBooking");
    if (qFrom && qTo && dayjs(qFrom).isValid() && dayjs(qTo).isValid()) {
      const padded = {
        from: dayjs(qFrom).subtract(2, "day").format(ISO_FMT),
        to: dayjs(qTo).add(5, "day").format(ISO_FMT),
      };
      aplicarRangoDeEnlace(padded);
      if (qPropertyId) setScrollToPropertyId(qPropertyId);
      // Capturamos el payload ANTES del setSearchParams({}) que limpia la query.
      // qFrom/qTo son el ingreso/salida reales del arriendo (los manda el
      // enlace de /arriendos).
      if (
        qEditBooking &&
        Number.isFinite(Number(qEditBooking)) &&
        qPropertyId &&
        Number.isFinite(Number(qPropertyId))
      ) {
        setPendingEdit({
          id: Number(qEditBooking),
          propertyId: Number(qPropertyId),
          ingreso: qFrom,
          salida: qTo,
        });
      }
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to target property row once calendar data is loaded
  useEffect(() => {
    if (!scrollToPropertyId || loading) return;
    requestAnimationFrame(() => {
      const el = wrapperRef.current?.querySelector<HTMLElement>(
        `[data-property-id="${scrollToPropertyId}"]`,
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Brief highlight to draw attention
        el.style.transition = "background-color 0.3s";
        el.style.backgroundColor = "rgba(71, 190, 245, 0.25)";
        setTimeout(() => { el.style.backgroundColor = ""; }, 2000);
      }
      setScrollToPropertyId(null);
    });
  }, [scrollToPropertyId, loading]);

  // Salta a un arriendo: mueve el rango a sus fechas (con padding), limpia los
  // filtros de fila (para que su propiedad no quede oculta) y programa el
  // scroll + resaltado. Reutiliza el mecanismo de `scrollToPropertyId`.
  //
  // Un salto es puntual, no una elección de filtros: todo lo de acá va con los
  // setters que NO guardan. Con los setters normales, a los 400 ms quedaban
  // guardados el vacío de las tres búsquedas y las fechas de esa estadía como
  // rango preferido — o sea que buscar un código le borraba para siempre los
  // filtros de trabajo al admin. Ver docs/filtros-persistentes.md §2.
  const jumpToBooking = (b: {
    id: number;
    idPropiedad: number | null | undefined;
    ingreso: string | null | undefined;
    salida: string | null | undefined;
  }) => {
    if (b.ingreso && b.salida) {
      aplicarRangoDeEnlace({
        from: dayjs(b.ingreso).subtract(2, "day").format(ISO_FMT),
        to: dayjs(b.salida).add(5, "day").format(ISO_FMT),
      });
    }
    // Limpia filtros de fila para garantizar que la propiedad objetivo se vea.
    setPropSearchSinRecordar("");
    setGuestSearchSinRecordar("");
    setPropFiltersSinRecordar({ region: [], comuna: [], sector: [], cliente: [] });
    if (b.idPropiedad != null) setScrollToPropertyId(String(b.idPropiedad));
    setHighlightBookingId(b.id);
  };

  // Buscador de estadías del calendario. Encuentra por CUALQUIER código:
  //   - Código de estadía AF interno (#1472 = booking.id)
  //   - Código de plataforma Airbnb/Booking (id_reserva_plataforma)
  //   - Ref. plataforma / ID de Stays (id_reservac, tipo JS11J)
  //   - Nombre del huésped (para paridad con el buscador de /arriendos)
  // Match por SUBSTRING (case-insensitive), con preferencia por el código de
  // estadía EXACTO. Busca primero en lo cargado (instantáneo) y, si no está en
  // el rango/filtros actuales, consulta el backend por TODO el histórico
  // (mismo `_q` que /arriendos, que ya cubre todos esos códigos).
  const handleCodeSearch = async (raw: string) => {
    const term = String(raw ?? "")
      .trim()
      .replace(/^#/, "")
      .trim();
    // Término vacío = no-op silencioso. `Input.Search` con `allowClear` dispara
    // `onSearch("")` al tocar la X para limpiar el campo; mostrar una alerta ahí
    // se sentía "raro" (el usuario no está buscando, solo vació el buscador).
    if (!term) return;
    const needle = term.toLowerCase();

    // Campos-código de un booking del calendario donde puede vivir el término.
    const matchesTerm = (bk: any) =>
      [
        bk.id,
        bk.idReservaPlataforma,
        bk.idReservaStays,
        bk.nombreHuesped,
      ].some((f) => f != null && String(f).toLowerCase().includes(needle));

    // 1) En memoria (lo que está cargado en el calendario ahora mismo).
    const loaded = calendarRows.flatMap((r) => r.bookings ?? []);
    const localMatches = loaded.filter(matchesTerm);
    const localHit =
      localMatches.find((bk) => String(bk.id) === term) ?? localMatches[0];
    if (localHit) {
      if (localMatches.length > 1) {
        getMsg()?.info(
          `${localMatches.length} coincidencias con "${term}". Te llevo a la Estadía #${localHit.id}.`,
        );
      }
      jumpToBooking({
        id: localHit.id,
        idPropiedad: (localHit as any).idPropiedad,
        ingreso: localHit.ingreso,
        salida: localHit.salida,
      });
      return;
    }

    // 2) Backend: busca en TODO el histórico (sin filtro de fecha). `_q` es el
    // mismo buscador de /arriendos: matchea código interno, códigos de
    // plataforma, huésped, propiedad y notas.
    try {
      setCodeSearching(true);
      const json = await finanzasEmpresaApi.searchArriendos({
        filters: { fields: { _q: term } },
        page: 1,
        page_size: 50,
      });
      const data: any[] = json?.data ?? [];
      if (!data.length) {
        getMsg()?.warning(
          `No se encontró ninguna estadía con "${term}" (código interno, de plataforma o huésped).`,
        );
        return;
      }
      const hit = data.find((d) => String(d.id) === term) ?? data[0];
      if (data.length > 1) {
        getMsg()?.info(
          `${data.length} coincidencias con "${term}". Te llevo a la Estadía #${hit.id}.`,
        );
      }
      jumpToBooking({
        id: hit.id,
        idPropiedad: hit.idPropiedad,
        ingreso: hit.ingreso,
        salida: hit.salida,
      });
    } finally {
      setCodeSearching(false);
    }
  };

  // Flash de la barra concreta una vez que el nuevo rango terminó de cargar.
  useEffect(() => {
    if (highlightBookingId == null || loading) return;
    // Pequeña espera para que las barras del nuevo rango estén pintadas.
    const t = setTimeout(() => {
      const bar = wrapperRef.current?.querySelector<HTMLElement>(
        `[data-booking-id="${highlightBookingId}"]`,
      );
      if (bar) {
        bar.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
        bar.classList.add("cal-bar-flash");
        setTimeout(() => bar.classList.remove("cal-bar-flash"), 2400);
      }
      setHighlightBookingId(null);
    }, 300);
    return () => clearTimeout(t);
  }, [highlightBookingId, loading]);

  const [selectedCell, setSelectedCell] = useState<CalendarCellData | null>(
    null,
  );
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [rangeDraft, setRangeDraft] = useState<RangeSelection | null>(null);

  const [createRange, setCreateRange] = useState<RangeSelection | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [metaModalProps, setMetaModalProps] = useState<{
    field: MetaField;
    dateRange: [string, string];
    propertyId: number;
    propertyLabel: string;
    initialCondicion?: { tipo: "porcentaje" | "monto"; valor: number } | null;
  } | null>(null);
  // Modal de cambio masivo (Fase 4): multi-propiedad, mismo endpoint.
  const [bulkMetaOpen, setBulkMetaOpen] = useState(false);

  // Propiedades cuya meta se está REFRESCANDO tras aplicar un cambio (el PATCH
  // ya confirmó; falta la re-lectura con los definitivos). Mientras están acá,
  // sus filas meta muestran spinner + pulso en vez de valores viejos "quietos".
  // Round 2026-07-02 (pedido del dueño: feedback de carga hasta el definitivo).
  const [metaRefreshingIds, setMetaRefreshingIds] = useState<Set<number>>(
    () => new Set(),
  );
  const markMetaRefreshing = (ids: number[], on: boolean) => {
    setMetaRefreshingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const [createMode, setCreateMode] = useState<"create" | "edit">("create");
  const [editInitialValues, setEditInitialValues] =
    useState<Partial<CreatePayload> | null>(null);
  const [rangeUi, setRangeUi] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs(range.from),
    dayjs(range.to),
  ]);

  // Atajo "Mes completo": el valor del MonthPicker se DERIVA del rango actual.
  // Si el rango es exactamente un mes calendario (día 1 → último día), el
  // picker resalta ese mes; si no (rango arbitrario), queda vacío. Así no hay
  // estado paralelo que mantener sincronizado. Round 2026-07-11.
  const monthShortcutValue = useMemo<Dayjs | null>(() => {
    const [f, t] = rangeUi;
    if (
      f &&
      t &&
      f.date() === 1 &&
      f.isSame(t, "month") &&
      f.isSame(t, "year") &&
      t.isSame(f.endOf("month"), "day")
    ) {
      return f;
    }
    return null;
  }, [rangeUi]);

  const wrapperRef = useRef<HTMLDivElement>(null!);
  const [clickAnchor, setClickAnchor] = useState<DOMRect | null>(null);

  useEffect(() => {
    setRangeUi([dayjs(range.from), dayjs(range.to)]);
  }, [range.from, range.to]);

  // Preserva el scroll del calendario a través de un refetch.
  //
  // Round 2026-05-30 — refactor del approach anterior con
  // `requestAnimationFrame` puro. Problema: RAF puede dispararse ANTES
  // de que React commitee al DOM los datos nuevos del refetch. Cuando
  // React aplica el render con la nueva data, el grid recalcula su
  // template, los rows pueden cambiar de cantidad u orden, y el
  // browser resetea el scrollTop/scrollLeft a 0. El scroll que
  // habíamos restaurado en el RAF queda pisado por el reset del
  // browser, y el usuario ve la grilla "volver al principio" al
  // eliminar un evento.
  //
  // Solución: guardar el target en un ref ANTES del refetch, y
  // restaurarlo en `useLayoutEffect` que dispara cuando
  // `staysCalendarData` cambia (señal de que los datos nuevos ya
  // llegaron). `useLayoutEffect` corre SÍNCRONO después del commit
  // del DOM y ANTES del paint del browser, así el usuario no ve el
  // flash de "scroll en 0 → scroll restaurado".
  //
  // Usamos `staysCalendarData` (no `refreshing`) como dependencia
  // porque es el state que cambia cuando llegan los datos nuevos —
  // `refreshing` también cambia pero puede flicker (true → false →
  // true) si hay reentradas, ensuciando el restore.
  const pendingScrollRestoreRef = useRef<{ top: number; left: number } | null>(
    null,
  );

  // Estado del botón "Refrescar" (muestra spinner mientras re-trae datos).
  const [manualRefreshing, setManualRefreshing] = useState(false);

  const refetchKeepScroll = async () => {
    const el = wrapperRef.current;
    if (el) {
      pendingScrollRestoreRef.current = {
        top: el.scrollTop,
        left: el.scrollLeft,
      };
    }
    // Refresh SUAVE (round 2026-07-02): antes era `refetch()` (duro), que
    // enciende `setLoading(true)` (closure stale de isInitial en el hook)
    // → el early-return `if (loading)` desmonta la grilla → las filas meta
    // expandidas se colapsan aunque el scroll se restaure. Regla del
    // proyecto: toda mutación refresca EN SU LUGAR. `refreshCalendar` trae
    // los mismos eventos pero con preserveMeta, sin desmontar nada;
    // `includeMeta: false` porque los callers de esta función mutan
    // EVENTOS (create/edit/delete/recalc), que no alteran precios — los
    // cambios de precio refrescan su meta por su propio onSuccess. El
    // guardado de scroll de arriba queda como cinturón de seguridad.
    await refreshCalendar({ includeMeta: false });
    // El useLayoutEffect de abajo restaura el scroll cuando
    // `staysCalendarData` cambie como consecuencia del refetch.
  };

  // Refresh SUAVE manual (botón "Refrescar"): re-trae eventos + precios de las
  // propiedades abiertas SIN recargar la página, sin colapsar lo expandido y
  // preservando el scroll. Guardamos el scroll antes; el useLayoutEffect de
  // abajo lo restaura cuando llega la data nueva (eventos o meta).
  const handleRefresh = async () => {
    if (manualRefreshing) return;
    const el = wrapperRef.current;
    if (el) {
      pendingScrollRestoreRef.current = {
        top: el.scrollTop,
        left: el.scrollLeft,
      };
    }
    setManualRefreshing(true);
    // La fila de Mercado también se vuelve a pedir (en su lugar), como el resto.
    if (mercadoOn) setMercadoToken((k) => k + 1);
    try {
      await refreshCalendar();
    } finally {
      setManualRefreshing(false);
    }
  };

  // Restaurar scroll del calendario después de que React commitee los
  // datos nuevos al DOM. Sólo restaura si hay un pendiente — limpia el
  // ref al consumirlo para no re-disparar en cambios subsiguientes que
  // no vengan de `refetchKeepScroll`.
  useLayoutEffect(() => {
    const pending = pendingScrollRestoreRef.current;
    if (!pending) return;
    const el = wrapperRef.current;
    if (el) {
      el.scrollTop = pending.top;
      el.scrollLeft = pending.left;
    }
    pendingScrollRestoreRef.current = null;
    // Disparamos en cambios de meta (`staysCalendarData`) Y de eventos
    // (`calendarRows`): el refresh suave puede actualizar solo eventos (cuando
    // no hay propiedades abiertas) o ambos. Todo gateado por el ref pendiente,
    // así que solo restaura cuando hubo un refresh explícito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staysCalendarData, calendarRows]);

  // Hoy en hora chilena y que cambia solo a medianoche: la grilla marca esa
  // columna aunque la pestaña quede abierta de un día para otro.
  const today = useHoyChile();

  // Props del modal de cambio masivo MEMOIZADAS. Antes se pasaban como literales
  // (`[range.from, range.to]` y `properties.map(...)`) creados en cada render:
  // como `dateRange` está en las deps del useEffect de reset de MetaEditModal,
  // cualquier re-render del calendario (p.ej. un tick WS CALENDAR_CHANGED)
  // re-disparaba el reset y BORRABA el precio escrito, los días y las
  // propiedades seleccionadas a mitad de edición. Memoizar estabiliza la
  // referencia: el reset solo corre cuando el rango cambia de verdad.
  const bulkDateRange = useMemo<[string, string]>(
    () => [range.from, range.to],
    [range.from, range.to],
  );
  const bulkProperties = useMemo(
    () =>
      properties.map((p) => ({
        id: p.id,
        nombre: p.nombre,
        idPropiedadCorto: p.idPropiedadCorto,
        precioMinimoNoche: p.precioMinimoNoche ?? null,
      })),
    [properties],
  );

  const dateIndexMap = useMemo(() => {
    const m = new Map<string, number>();
    dates.forEach((d, i) => m.set(d, i));
    return m;
  }, [dates]);

  const mergeCellsForPropertyDay = (
    propertyId: number,
    date: string,
    rows: CalendarRow[],
  ): CalendarCellData => {
    const thirds: Record<
      CellThirdKey,
      { state: CellState; events?: CalendarEventRef[] }
    > = {
      morning: { state: "empty", events: [] },
      mid: { state: "empty", events: [] },
      evening: { state: "empty", events: [] },
    };

    const merged: CalendarCellData = {
      idPropiedad: propertyId,
      date,
      thirds,
      hasAny: false,
    };

    // Merge por tercio
    for (const row of rows) {
      const cell = getCell(row, date);

      merged.nombrePropiedad ??= cell.nombrePropiedad ?? row.propertyName;

      (Object.keys(cell.thirds) as CellThirdKey[]).forEach((k) => {
        const src = cell.thirds[k];
        const dst = merged.thirds[k];

        // estado: si estaba vacío, toma el del track
        if (dst.state === "empty" && src.state !== "empty") {
          dst.state = src.state;
        }

        if (src.events?.length) {
          dst.events = [...(dst.events ?? []), ...src.events];
          merged.hasAny = true;
        }
      });
    }

    const allBookingEvents = [
      ...(merged.thirds.morning.events ?? []),
      ...(merged.thirds.mid.events ?? []),
      ...(merged.thirds.evening.events ?? []),
    ].filter((e) => e.kind === "booking") as Extract<
      CalendarEventRef,
      { kind: "booking" }
    >[];

    const pick = (ev: Extract<CalendarEventRef, { kind: "booking" }>) => ({
      reservaId: ev.bookingId,
      plataforma: ev.plataforma,
      codigoPlataforma: ev.codigoPlataforma,
      huesped: ev.huesped,
      telefono: ev.telefono,
      reservaTotalClp: ev.reservaTotalClp,
      precioPorNoche: ev.precioPorNoche,
    });

    const inEv = allBookingEvents.find((b) => b.ingreso === date);
    const outEv = allBookingEvents.find((b) => b.salida === date);
    if (inEv) merged.checkin = pick(inEv);
    if (outEv) merged.checkout = pick(outEv);

    return merged;
  };

  const propertyNameById = useMemo(() => {
    const map = new Map<number, string>();
    (properties ?? []).forEach((p: Property) => map.set(p.id, p.nombre));
    return map;
  }, [properties]);

  // Ref a `onEditEvent` (definido en la rama admin, más abajo). El effect de
  // deep-link vive ACÁ ARRIBA porque un hook no puede ir después del early
  // return `if (!isAdmin)`; el ref se rellena en cada render admin.
  const onEditEventRef = useRef<
    ((ev: CalendarEventRef, slots: CellThirdKey[]) => void) | null
  >(null);

  // Deep-link de edición (`/calendario?editBooking=<id>&propertyId=<p>&from=<ing>&to=<sal>`,
  // lo usa el enlace por fila de /arriendos). Reutiliza el flujo de edición
  // real (`onEditEvent` vía ref: fetch de detalle, construcción de
  // initialValues, checks de solape/cascadas) pero SIN depender de encontrar
  // el booking en `calendarRows` (que carga async y se refresca → carreras).
  // El `ev` lo sintetizamos desde el payload del deep-link; si el booking ya
  // está cargado usamos su ref real (mejores fallbacks). onEditEvent completa
  // el resto con fetchEventDetail(id). Se dispara EXACTAMENTE una vez
  // (editFiredRef) para no re-abrir ni apilar el toast "Cargando detalle…".
  // Dos fases: seteamos `selectedCell` y recién en la re-corrida disparamos
  // (onEditEvent lee `selectedCell` del estado — evita el closure viejo).
  useEffect(() => {
    if (!pendingEdit || editFiredRef.current) return;
    // Esperamos a que carguen las propiedades: openCreateModal toma de ahí los
    // subitems de limpieza (transporte/materiales/lavandería) para el diagrama.
    if (!properties || properties.length === 0) return;
    const { id, propertyId, ingreso } = pendingEdit;
    // Celda del día de ingreso — estructura válida aunque la data no haya
    // cargado (mergeCellsForPropertyDay devuelve una celda vacía consistente).
    const rowsOfProperty = calendarRows.filter((r) => r.propertyId === propertyId);
    const cell = mergeCellsForPropertyDay(propertyId, ingreso, rowsOfProperty);
    // ev: ref real del booking si ya está en la data (mejores fallbacks de
    // huésped/precio); si no, sintético — onEditEvent completa vía fetchEventDetail.
    const foundEv = [
      ...(cell.thirds.evening?.events ?? []),
      ...(cell.thirds.morning?.events ?? []),
      ...(cell.thirds.mid?.events ?? []),
    ].find((e: any) => e?.kind === "booking" && e?.bookingDbId === id);
    const ev = (foundEv as CalendarEventRef) ?? ({
      kind: "booking",
      bookingDbId: id,
      ingreso: pendingEdit.ingreso,
      salida: pendingEdit.salida,
    } as CalendarEventRef);
    // Fase 1: `selectedCell` debe ser la celda del booking (onEditEvent lo lee
    // del estado) antes de disparar.
    if (
      selectedCell?.idPropiedad !== propertyId ||
      selectedCell?.date !== ingreso
    ) {
      setSelectedCell(cell);
      return; // re-corre cuando `selectedCell` cambie
    }
    // Fase 2: disparar el flujo de edición real UNA sola vez.
    editFiredRef.current = true;
    onEditEventRef.current?.(ev, ["evening"]);
    setPendingEdit(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEdit, properties, calendarRows, selectedCell]);

  // ── Bar menu actions (3-dot menu) — must be before early return ──
  const barMenuActions = useMemo(
    () => ({
      onViewFinanzas: (propertyId: number, date: string, bookingId?: number) => {
        const base = dayjs(date);
        const from = base.subtract(5, "day").format("YYYY-MM-DD");
        const to = base.add(5, "day").format("YYYY-MM-DD");
        const params = new URLSearchParams({ from, to });
        // `bookingId` prefiltra la tabla de /arriendos a SOLO ese arriendo;
        // sin él caemos al filtro por propiedad como antes.
        if (bookingId != null) {
          params.set("bookingId", String(bookingId));
        } else {
          params.set("propiedadId", String(propertyId));
        }
        navigate(`/arriendos?${params.toString()}`);
      },
      onEditBooking: async (booking: any, propertyIdArg?: number) => {
        // propertyId ya NO viene siempre dentro de `b` (el grid pasa b
        // crudo de la reserva; id_propiedad no está en ese payload).
        // Aceptamos propertyIdArg del caller (calendarioGrid pasa
        // row.propertyId) con fallback a los campos legacy del objeto.
        const propertyId =
          propertyIdArg ?? booking.id_propiedad ?? booking.propertyId;
        const ingreso = booking.ingreso;
        const salida = booking.salida;
        const id = booking.id ?? booking.bookingDbId;
        if (!propertyId || !ingreso || !salida || !id) {
          getMsg()?.warning("No se pudo abrir el editor para esta reserva");
          return;
        }

        // Full hydration desde backend: sin esto, la ganancia Aloja Fácil,
        // comisiones, extras, descuentos y subitems de limpieza llegan al
        // modal en 0/vacío y el auto-calc no puede computar nada útil.
        const hide = getMsg()?.loading("Cargando detalle para editar…", 0);
        let detail: any = null;
        try {
          detail = await fetchEventDetail("booking", id);
        } catch (e: any) {
          console.error("[onEditBooking] fetchEventDetail falló:", e);
          getMsg()?.warning(
            "No pude cargar el detalle completo; abriré el editor con los datos visibles.",
          );
        }
        const d = detail ?? {};

        // Tercio de salida = derivado de hora_nueva si hay salida tardía,
        // sino "morning" (check-out estándar 10:59).
        const horaNuevaStr: string | undefined =
          d?.adicionalesArriendo?.salida_tardia?.hora_nueva;
        const tercioFromHHmm = (hhmm?: string) => {
          if (!hhmm) return "morning" as const;
          const [h, m] = hhmm.slice(0, 5).split(":").map(Number);
          const min = (h || 0) * 60 + (m || 0);
          if (min < 11 * 60) return "morning" as const;
          if (min < 15 * 60) return "mid" as const;
          return "evening" as const;
        };

        try {
          setCreateMode("edit");
          setEditInitialValues({
            id,
            kind: "booking",
            startThird: "evening",
            endThird: tercioFromHHmm(horaNuevaStr),
            ingreso: dayjs(d.ingreso ?? ingreso) as any,
            salida: dayjs(d.salida ?? salida) as any,
            nombreHuesped:
              d.nombreHuesped ??
              booking.nombreHuesped ??
              booking.nombre_huesped ??
              "",
            telefonoHuesped:
              d.telefonoHuesped ??
              booking.telefonoHuesped ??
              booking.telefono_huesped ??
              "",
            cantidadHuespedes: d.cantidadHuespedes ?? 1,
            estado: d.estado ?? "booked",
            precioNocheClp:
              (d.precioNocheClp && d.precioNocheClp > 0
                ? d.precioNocheClp
                : null) ??
              (booking.precioNocheClp ?? booking.precio_noche_clp) ??
              0,
            limpiezaClp:
              d.limpiezaClp ??
              booking.limpiezaClp ??
              booking.limpieza_clp ??
              0,
            comisionPlataformaClp: d.comisionPlataformaClp ?? 0,
            extrasSinComisionClp: Array.isArray(d.extrasSinComisionClp)
              ? d.extrasSinComisionClp
              : [],
            extrasConComisionClp: Array.isArray(d.extrasConComisionClp)
              ? d.extrasConComisionClp
              : [],
            descuentoClp: Array.isArray(d.descuentoClp) ? d.descuentoClp : [],
            estadoPago: d.estadoPago ?? undefined,
            tipoPago: d.tipoPago ?? undefined,
            notaInterna: d.notaInterna ?? "",
            // Fix 2026-08-13 — origen del arriendo. El autocalc del modal lo
            // usa para saber si `precioNochesClp` viene BRUTO (manual
            // "Directa") o ya NETO de comisión de plataforma (API). No se
            // re-persiste en edición (buildPayload lo omite en edit).
            nombrePlataforma:
              d.nombrePlataforma ??
              (booking as any).nombrePlataforma ??
              (booking as any).nombre_plataforma ??
              undefined,
            ...(d.limpiezaSubitemsOverride
              ? {
                  limpiezaPreset:
                    d.limpiezaSubitemsOverride.preset ?? "default",
                  limpiezaCustomAseador:
                    d.limpiezaSubitemsOverride.aseador ?? 0,
                  limpiezaCustomTransporteTotal:
                    d.limpiezaSubitemsOverride.transporte_total ?? 0,
                  limpiezaCustomMaterialesTotal:
                    d.limpiezaSubitemsOverride.materiales_total ?? 0,
                  limpiezaCustomLavanderiaTotal:
                    d.limpiezaSubitemsOverride.lavanderia_total ?? 0,
                }
              : { limpiezaPreset: "default" }),
            extensionesNoches: Array.isArray(
              d.adicionalesArriendo?.extensiones_noches,
            )
              ? d.adicionalesArriendo.extensiones_noches
              : [],
            salidaTardia: d.adicionalesArriendo?.salida_tardia ?? {
              monto_clp: 0,
            },
            otrosCargos: Array.isArray(d.adicionalesArriendo?.otros)
              ? d.adicionalesArriendo.otros
              : [],
          } as any);
          openCreateModal(propertyId, ingreso, salida);
        } catch (e: any) {
          console.error("[onEditBooking] no pude abrir el editor:", e);
          getMsg()?.error(e?.message ?? "No pude abrir el editor");
        } finally {
          hide?.();
        }
      },
      onEditEvent: async (event: any) => {
        const propertyId = event.propertyId;
        const startDate = event.startISO?.slice(0, 10);
        const endDate = event.endISO?.slice(0, 10);
        if (!propertyId || !startDate || !endDate) {
          getMsg()?.warning("No se pudo abrir el editor para este evento");
          return;
        }
        setCreateMode("edit");
        const kind = event.kind === "blocked" ? "block" : event.kind;

        if (kind === "cleaning") {
          // Igual que onEditBooking y que el onEditEvent del modal de detalle:
          // hidratamos el detalle del backend ANTES de abrir el editor. Sin
          // esto, el menú de la barra sembraba un set mínimo (sin `pagado_por`
          // ni `idAseador` ni subitems) y al re-guardar PISABA el pagador real
          // con "AF" (tipo=0) y desasignaba al aseador. Además sembramos
          // `reservaId` para que el modal sepa si el aseo es VINCULADO y
          // deshabilite "Propietario/Cliente paga" (anti-doble-cobro §6.4).
          const hide = getMsg()?.loading("Cargando detalle para editar…", 0);
          let detail: any = null;
          try {
            detail = await fetchEventDetail("cleaning", event.id);
          } catch (e: any) {
            console.error("[onEditEvent] fetchEventDetail (cleaning) falló:", e);
            getMsg()?.warning(
              "No pude cargar el detalle completo; abriré el editor con los datos visibles.",
            );
          } finally {
            hide?.();
          }
          const d = detail ?? {};
          const propForAseo = properties.find((p) => p.id === propertyId);

          // Mismo desglose que el handler del modal de detalle: si hay
          // subitems_override usamos preset="custom" con los valores precisos;
          // si no, "default" con el aseador stored + el resto de la propiedad.
          const aseoBreakdown = d.subitemsOverride
            ? {
                aseoCostPreset: "custom" as const,
                aseoCustomAseador: d.subitemsOverride.aseador ?? 0,
                aseoCustomTransporteTotal:
                  d.subitemsOverride.transporte_total ?? 0,
                aseoCustomMaterialesTotal:
                  d.subitemsOverride.materiales_total ?? 0,
                aseoCustomLavanderiaTotal:
                  d.subitemsOverride.lavanderia_total ?? 0,
              }
            : {
                aseoCostPreset: "default" as const,
                aseoCustomAseador:
                  d.sueldoAseador ?? (propForAseo as any)?.sueldoAseo ?? 0,
                aseoCustomTransporteTotal:
                  (propForAseo as any)?.transporteTotal ?? 0,
                aseoCustomMaterialesTotal:
                  (propForAseo as any)?.materialesTotal ?? 0,
                aseoCustomLavanderiaTotal:
                  (propForAseo as any)?.lavanderiaTotal ?? 0,
              };

          // Round 2026-08-25 — equipo 1..N con reparto: hidratar el editor
          // multi-aseadora desde el detalle vía el seed compartido
          // (utils/aseos.utils — review 2026-09-07: los dos caminos de
          // hidratación copiaban este bloque). `repartoTocado` nace false: si
          // el admin no toca el equipo, el payload NO manda `asignaciones` y
          // el backend preserva (y re-escala) el reparto vigente.
          setEditInitialValues({
            id: event.id,
            kind,
            ingreso: dayjs(d.fecha ?? startDate),
            salida: dayjs(d.fechaFin ?? endDate),
            modoAsignacion: d.idAseador ? "designado" : "abierto",
            idAseador: d.idAseador ?? undefined,
            ...seedEquipoAseo(d),
            costoLimpieza: d.costoLimpieza ?? event.costoLimpieza ?? 0,
            sueldoAseador: d.sueldoAseador ?? event.sueldoAseador ?? 0,
            urgencia: d.urgencia ?? event.urgencia ?? "am",
            estado: d.estado ?? event.estado ?? "pendiente",
            observaciones: d.observaciones ?? event.notas ?? "",
            pagado_por: d.pagado_por ?? { tipo: 0 },
            reservaId: event.reservaId ?? (d as any).reservaId ?? null,
            // Fallback degradado: sin detalle real, el desglose sembrado es
            // adivinado (preset "default" aunque el aseo tenga override). El
            // modal OMITE las claves del desglose en el payload salvo que el
            // admin toque el Modo — evita un reset financiero no pedido
            // (round 2026-08-02).
            aseoDetalleIncompleto: detail == null,
            ...aseoBreakdown,
          } as any);
          openCreateModal(propertyId, startDate, endDate);
          return;
        }

        // block / maintenance: hidratar fechas + tercios + horas REALES del
        // bloqueo. Antes solo se sembraba id/kind/razon/notas y los
        // DatePickers de Entrada/Salida arrancaban VACÍOS → imposible editar
        // el rango (bug 2026-07-03). Traemos el detalle del backend
        // (horaInicio/horaTermino en HH:mm sin timezone, fechaInicio/
        // fechaTermino en YYYY-MM-DD) — mismo patrón que cleaning/booking.
        const hideBlk = getMsg()?.loading("Cargando detalle para editar…", 0);
        let blkDetail: any = null;
        try {
          blkDetail = await fetchEventDetail(kind, event.id);
        } catch (e: any) {
          console.error("[onEditEvent] fetchEventDetail (block) falló:", e);
        } finally {
          hideBlk?.();
        }
        const bd = blkDetail ?? {};
        const bStart = bd.fechaInicio ?? bd.startDate ?? startDate;
        const bEnd = bd.fechaTermino ?? bd.endDate ?? endDate;
        const bHoraIni = bd.horaInicio ?? bd.startTime ?? "15:00";
        const bHoraFin = bd.horaTermino ?? bd.endTime ?? "11:00";
        setEditInitialValues({
          id: event.id,
          kind,
          razon: bd.razon ?? event.razon ?? "",
          notas: bd.notas ?? event.notas ?? "",
          ingreso: dayjs(bStart) as any,
          salida: dayjs(bEnd) as any,
          startThird: tercioDeHHmm(bHoraIni) as any,
          // endThird usa el mapeo de TÉRMINO (borde half-open): "11:00" = fin
          // de mañana, no inicio de mediodía. Sin esto el bloqueo se extendía
          // un tercio al guardar.
          endThird: tercioFinDeHHmm(bHoraFin) as any,
          startTime: bHoraIni,
          endTime: bHoraFin,
        } as any);
        openCreateModal(propertyId, bStart, bEnd);
      },
      onDeleteBooking: (
        bookingId: number,
        opts?: { preserveCleaning?: boolean },
      ) => {
        // Dos modos según el ítem del menú clickeado:
        //   - preserveCleaning=false (default): cascada completa — borra
        //     reserva, aseo AUTO, bloqueo salida tardía y cargos linkeados.
        //   - preserveCleaning=true: solo desvincula el aseo AUTO
        //     (reserva_id=null) para conservarlo como evento independiente.
        // El backend (stays.controller.deleteEvent) soporta ambas vías —
        // ver docs/calendario-cascada-arriendos.md §2.
        if (!bookingId) return;
        const preserveCleaning = opts?.preserveCleaning === true;
        getModal().confirm({
          title: preserveCleaning
            ? "¿Eliminar solo el arriendo?"
            : "¿Eliminar este arriendo?",
          content: preserveCleaning ? (
            <div>
              <p>
                Se va a eliminar <strong>solo la reserva</strong>. El aseo AUTO
                vinculado quedará como evento independiente (sin{" "}
                <code>reserva_id</code>) para conservar su asignación o
                ejecución.
              </p>
              <p style={{ marginBottom: 0 }}>
                También se eliminan: el bloqueo de salida tardía y los cargos
                adicionales.
              </p>
            </div>
          ) : (
            "Esta acción no se puede deshacer. Se eliminan también el aseo AUTO, el bloqueo de salida tardía y los cargos adicionales."
          ),
          okText: preserveCleaning
            ? "Eliminar solo arriendo"
            : "Eliminar arriendo y aseo",
          okType: "danger",
          cancelText: "Cancelar",
          centered: true,
          async onOk() {
            try {
              await deleteEvent("booking", bookingId, { preserveCleaning });
              getMsg()?.success(
                preserveCleaning
                  ? "Arriendo eliminado · aseo conservado"
                  : "Reserva eliminada",
              );
              // deleteEvent ya dispara refetchKeepScroll internamente.
            } catch {
              getMsg()?.error("Error al eliminar");
            }
          },
        });
      },
      onDeleteEvent: (event: any) => {
        getModal().confirm({
          title: "¿Eliminar este evento?",
          content: "Esta acción no se puede deshacer.",
          okText: "Eliminar",
          okType: "danger",
          cancelText: "Cancelar",
          centered: true,
          async onOk() {
            try {
              await deleteEvent(event.kind, event.id);
              getMsg()?.success("Evento eliminado");
              // deleteEvent ya dispara refetchKeepScroll internamente.
            } catch {
              getMsg()?.error("Error al eliminar");
            }
          },
        });
      },
      onCopyInfo: (booking: any) => {
        const fmtDate = (d: string) => {
          if (!d) return "-";
          const p = d.split("-");
          return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : d;
        };
        const lines = [
          `Huésped: ${booking.nombreHuesped ?? "-"}`,
          `Teléfono: ${formatPhoneDisplay(booking.telefonoHuesped) || "-"}`,
          `Plataforma: ${formatPlataforma(booking.nombrePlataforma)}`,
          `Código: ${booking.idReservaPlataforma ?? booking.idReservaStays ?? "-"}`,
          `Entrada: ${fmtDate(booking.ingreso)}`,
          `Salida: ${fmtDate(booking.salida)}`,
          `Total: ${booking.reservaTotalClp != null ? `$${Number(booking.reservaTotalClp).toLocaleString("es-CL")}` : "-"}`,
        ];
        navigator.clipboard.writeText(lines.join("\n"));
        getMsg()?.success("Información copiada");
      },
      // Mismo helper que el botón del modal de detalle: lee el mensaje del
      // listado (o de la propiedad fresca) y avisa éxito / motivo del fallo.
      onCopyInstructions: (propertyId: number) => {
        void copiarInstruccionesDePropiedad(propertyId, properties);
      },
    }),
    [navigate, properties, deleteEvent, refetch],
  );

  // (La bifurcación por rol vive ahora en el dispatcher `Calendario` de arriba;
  // `CalendarioAdmin` solo se monta para admin/superadmin.)

  const openCreateModal = (
    propertyId: number,
    startDate: string,
    endDate: string,
    presetThirds?: {
      startThird: "morning" | "mid" | "evening";
      endThird: "morning" | "mid" | "evening";
    },
    tasaComisionAf?: number,
    costoAseo?: number,
    sueldoAseo?: number,
    // Presets adicionales para el caso "Día completo" (y cualquier
    // otro flujo que quiera forzar kind + horas al abrir). El modal
    // los lee desde `range.presetKind/presetStartTime/presetEndTime`.
    presets?: {
      presetKind?: "cleaning" | "booking" | "block" | "maintenance";
      presetStartTime?: string;
      presetEndTime?: string;
    },
  ) => {
    // Inyectamos los subitems de limpieza desde la propiedad para que el
    // modal pueda calcular el Ingreso AF y el diagrama del reparto.
    // Ver docs/modulo-arriendos-precios.md §Desglose de limpieza.
    const prop = properties.find((p) => p.id === propertyId);
    // tasaComisionAf / costoAseo / sueldoAseo: si el caller no los pasó
    // explícitos (p.ej. desde `onEditEvent` que abre el modal sin esos
    // params), hacemos fallback a los valores de la propiedad. Sin este
    // fallback, el desglose del aseo en edit-mode mostraba $0 como pago
    // al aseador porque `range.sueldoAseo` quedaba undefined.
    const effTasaComisionAf =
      tasaComisionAf ?? prop?.tasaComisionAf ?? undefined;
    const effCostoAseo = costoAseo ?? prop?.costoAseo ?? undefined;
    const effSueldoAseo = sueldoAseo ?? prop?.sueldoAseo ?? undefined;
    setCreateRange({
      propertyId,
      propertyName: propertyNameById.get(propertyId) ?? null,
      startDate,
      endDate,
      ...(effTasaComisionAf != null ? { tasaComisionAf: effTasaComisionAf } : {}),
      ...(effCostoAseo != null ? { costoAseo: effCostoAseo } : {}),
      ...(effSueldoAseo != null ? { sueldoAseo: effSueldoAseo } : {}),
      ...(prop?.transporteNeto != null
        ? { transporteNeto: prop.transporteNeto }
        : {}),
      ...(prop?.transporteTotal != null
        ? { transporteTotal: prop.transporteTotal }
        : {}),
      ...(prop?.materialesNeto != null
        ? { materialesNeto: prop.materialesNeto }
        : {}),
      ...(prop?.materialesTotal != null
        ? { materialesTotal: prop.materialesTotal }
        : {}),
      ...(prop?.lavanderiaNeto != null
        ? { lavanderiaNeto: prop.lavanderiaNeto }
        : {}),
      ...(prop?.lavanderiaTotal != null
        ? { lavanderiaTotal: prop.lavanderiaTotal }
        : {}),
      hasLavanderiaAf: Boolean(prop?.hasLavanderiaAf),
      ...(presetThirds
        ? {
            startThird: presetThirds.startThird,
            endThird: presetThirds.endThird,
          }
        : {}),
      ...(presets ?? {}),
    });
    setIsCreateOpen(true);
  };

  const thirdFromSlots = (slots: Array<CellThirdKey>) => {
    const order: CellThirdKey[] = ["morning", "mid", "evening"];
    const min = Math.min(...slots.map((s) => order.indexOf(s)));
    const max = Math.max(...slots.map((s) => order.indexOf(s)));
    return { startThird: order[min], endThird: order[max] };
  };

  const dateFromISO = (iso?: string | null) => (iso ? iso.slice(0, 10) : null);

  const clearRange = () => {
    setRangeDraft(null);
    setClickAnchor(null);
  };

  const handleWrapperScroll = () => {
    if (clickAnchor) setClickAnchor(null);
    if (rangeDraft) clearRange();
  };

  const bitForThird = (t: CellThirdKey) =>
    t === "morning" ? 1 : t === "mid" ? 2 : 4;

  const maskFromThirdToEnd = (t: CellThirdKey) =>
    t === "morning" ? 7 : t === "mid" ? 6 : 4;

  const maskFromStartToThird = (t: CellThirdKey) =>
    t === "morning" ? 1 : t === "mid" ? 3 : 7;

  const maskBetweenThirds = (a: CellThirdKey, b: CellThirdKey) => {
    const order: CellThirdKey[] = ["morning", "mid", "evening"];
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    const lo = Math.min(ia, ib);
    const hi = Math.max(ia, ib);
    let m = 0;
    for (let i = lo; i <= hi; i++) m |= bitForThird(order[i]);
    return m;
  };

  const occupiedMaskOfDay = (merged: CalendarCellData) => {
    let m = 0;
    (["morning", "mid", "evening"] as CellThirdKey[]).forEach((t) => {
      const th = merged.thirds[t];
      const hasEvents = (th.events?.length ?? 0) > 0;
      const hasState = th.state !== "empty";
      if (hasEvents || hasState) m |= bitForThird(t);
    });
    return m;
  };

  /**
   * Helpers reutilizables para validación admin en CalendarCreateModal.
   * Expuestos por prop — el modal no tiene acceso directo al estado del
   * calendario, así que los inyectamos desde acá.
   *
   * NOTA: son funciones planas (no `useCallback`) porque viven DESPUÉS
   * del early return `if (!isAdmin) return <CalendarioClienteMonth />` y
   * las reglas de hooks de React prohíben hooks condicionales. Como se
   * recrean en cada render, el modal re-renderiza cuando cambia el
   * estado del calendario — que es el comportamiento deseado de todas
   * formas (ocupación de tercios se refresca).
   */

  // ¿La propiedad `propertyId` en la fecha `iso` tiene ALGUNA parte
  // ocupada (cualquier tercio con eventos)? Se usa para ocultar el
  // botón "Día completo" del popover rápido.
  const hasAnyOccupiedTercio = (propertyId: number, iso: string): boolean => {
    const rowsOfProperty = calendarRows.filter(
      (r) => r.propertyId === propertyId,
    );
    if (rowsOfProperty.length === 0) return false;
    const merged = mergeCellsForPropertyDay(propertyId, iso, rowsOfProperty);
    return occupiedMaskOfDay(merged) !== 0;
  };

  // ¿`ev` es el evento que se está editando (o un satélite suyo)? Compartido
  // por checkAdminOverlap y por isDayFullyOccupied en modo edición: reeditar
  // un evento no debe chocar (ni deshabilitar fechas) consigo mismo. Cuando
  // se edita un booking, sus satélites (aseo AUTO + bloqueo AUTO, vinculados
  // por `reservaId`) también se excluyen — la cascada los recalcula junto
  // con el booking (docs/calendario-cascada-arriendos.md §5.1 / §6.2).
  const isExcludedEvent = (
    ev: CalendarEventRef,
    excludeId?: number | string | null,
    excludeKind?: string | null,
  ): boolean => {
    if (excludeId == null || !excludeKind) return false;
    const norm = (k: string) => (k === "blocked" ? "block" : k);
    const evId =
      ev.kind === "booking"
        ? (ev.bookingDbId ?? ev.bookingId ?? null)
        : ev.kind === "cleaning"
          ? (ev.cleaningId ?? null)
          : ((ev as any).id ?? null);
    if (
      evId != null &&
      String(evId) === String(excludeId) &&
      norm(ev.kind) === norm(excludeKind)
    ) {
      return true;
    }
    if (norm(excludeKind) === "booking") {
      const resId = (ev as any).reservaId ?? null;
      if (resId != null && String(resId) === String(excludeId)) return true;
    }
    return false;
  };

  // ¿Los 3 tercios están ocupados? La fecha no sirve ni como inicio ni
  // como fin porque no hay tercio libre. Usada por el DatePicker del modal
  // para `disabledDate`.
  //
  // `exclude` (round 2026-07-03): en modo EDICIÓN el modal pasa el id+kind
  // del evento que se está editando. Sin esto, los días cubiertos por el
  // PROPIO bloqueo aparecían deshabilitados en los date-pickers y era
  // imposible achicarlo dentro de su período (el bloqueo aún existe en el
  // calendario mientras se edita). En ese modo el tercio cuenta ocupado
  // solo si tiene ALGÚN evento ajeno — se ignora `state`, que no es
  // atribuible a un evento (misma base que usa checkAdminOverlap).
  const isDayFullyOccupied = (
    propertyId: number,
    iso: string,
    exclude?: { id?: number | string | null; kind?: string | null },
  ): boolean => {
    const rowsOfProperty = calendarRows.filter(
      (r) => r.propertyId === propertyId,
    );
    if (rowsOfProperty.length === 0) return false;
    const merged = mergeCellsForPropertyDay(propertyId, iso, rowsOfProperty);
    if (!exclude || exclude.id == null) {
      return occupiedMaskOfDay(merged) === 7; // 1|2|4 = los 3 tercios
    }
    let m = 0;
    (["morning", "mid", "evening"] as CellThirdKey[]).forEach((t) => {
      const evs = merged.thirds[t].events ?? [];
      if (evs.some((ev) => !isExcludedEvent(ev, exclude.id, exclude.kind))) {
        m |= bitForThird(t);
      }
    });
    return m === 7;
  };

  // Overlap a nivel de tercio para admin/superadmin. Devuelve el primer
  // conflicto como string humano, o null si todo está libre. Ignora el
  // evento que estamos editando (excludeId/excludeKind) para que reeditar
  // uno existente no choque consigo mismo.
  const checkAdminOverlap = (opts: {
    propertyId: number;
    startDate: string;
    endDate: string;
    startThird: CellThirdKey;
    endThird: CellThirdKey;
    excludeId?: number | string | null;
    excludeKind?: string | null;
  }): string | null => {
    const {
      propertyId,
      startDate,
      endDate,
      startThird,
      endThird,
      excludeId,
      excludeKind,
    } = opts;
    const rowsOfProperty = calendarRows.filter(
      (r) => r.propertyId === propertyId,
    );
    if (rowsOfProperty.length === 0) return null;

      const thirdLabel: Record<CellThirdKey, string> = {
        morning: "mañana",
        mid: "mediodía",
        evening: "tarde",
      };
      const describe = (ev: CalendarEventRef): string => {
        switch (ev.kind) {
          case "booking":
            return "un arriendo";
          case "cleaning":
            return "un aseo";
          case "maintenance":
            return "una mantención";
          default:
            return "un bloqueo";
        }
      };
      const sIdx = dateIndexMap.get(startDate);
      const eIdx = dateIndexMap.get(endDate);
      // Defensa en profundidad (fix 2026-07-03): NO fallar-abierto cuando un
      // extremo del bloqueo cae fuera de la ventana renderizada. Antes esto
      // hacía `return null` (sin validar) y, combinado con que el backend no
      // validaba bloqueos de admin, dejaba MOVER/EXTENDER un bloqueo ENCIMA de
      // fechas ocupadas sin ningún aviso. Ahora validamos la porción VISIBLE
      // del rango con la máscara correcta por día; la validación autoritativa
      // de TODO el rango (sin depender de la ventana) la hace el backend en el
      // POST/PATCH.
      if (dates.length === 0) return null;
      const lastIdx = dates.length - 1;
      // Si el bloqueo no intersecta la ventana cargada, no hay nada visible
      // que validar acá — el backend decide (comparación lexicográfica de
      // "YYYY-MM-DD").
      if (endDate < dates[0] || startDate > dates[lastIdx]) return null;
      // Índices lógicos REALES del rango (clasifican cada día como
      // inicio/fin/interior). Si un extremo cae fuera de la ventana usamos un
      // sentinela fuera de rango: ese día nunca será "inicio"/"fin", así que
      // los días visibles de ese lado se tratan como INTERIOR (máscara 7).
      const realLo = sIdx ?? -1;
      const realHi = eIdx ?? lastIdx + 1;
      // Recorrido acotado a la porción visible.
      const lo = Math.max(0, sIdx ?? 0);
      const hi = Math.min(lastIdx, eIdx ?? lastIdx);

      for (let i = lo; i <= hi; i++) {
        const iso = dates[i];
        const merged = mergeCellsForPropertyDay(
          propertyId,
          iso,
          rowsOfProperty,
        );
        const reqMask = requestedMaskForDay(
          i,
          realLo,
          realHi,
          startThird,
          endThird,
        );
        const thirdsToCheck: CellThirdKey[] = [];
        if (reqMask & 1) thirdsToCheck.push("morning");
        if (reqMask & 2) thirdsToCheck.push("mid");
        if (reqMask & 4) thirdsToCheck.push("evening");

        for (const t of thirdsToCheck) {
          const events = merged.thirds[t].events ?? [];
          for (const ev of events) {
            // El propio evento en edición — o un satélite del booking en
            // edición (aseo AUTO / bloqueo AUTO por `reservaId`) — nunca
            // cuenta como conflicto. Lógica compartida en isExcludedEvent.
            // Cualquier OTRO evento (otro bloqueo, arriendo, aseo) SÍ es
            // conflicto: mover un bloqueo NO puede pisar otro evento.
            if (isExcludedEvent(ev, excludeId, excludeKind)) {
              continue;
            }
            return `Se superpone con ${describe(ev)} el ${iso} (${thirdLabel[t]}).`;
          }
        }
      }
      return null;
  };

  const requestedMaskForDay = (
    dayIdx: number,
    startIdx: number,
    endIdx: number,
    startThird: CellThirdKey,
    endThird: CellThirdKey,
  ) => {
    if (startIdx === endIdx) return maskBetweenThirds(startThird, endThird);
    if (dayIdx === startIdx) return maskFromThirdToEnd(startThird);
    if (dayIdx === endIdx) return maskFromStartToThird(endThird);
    return 7;
  };

  const onCellDoubleClick = (
    cell: CalendarCellData,
    clickedThird: CellThirdKey,
    _e: React.MouseEvent,
  ) => {
    clearRange();
    const prop = properties.find((p) => p.id === cell.idPropiedad);
    openCreateModal(
      cell.idPropiedad,
      cell.date,
      cell.date,
      { startThird: clickedThird, endThird: clickedThird },
      prop?.tasaComisionAf ?? undefined,
      prop?.costoAseo ?? undefined,
      prop?.sueldoAseo ?? undefined,
    );
    // Set cleaning as default kind via editInitialValues
    setEditInitialValues({ kind: "cleaning" } as any);
  };

  const onCellContextMenu = () => {
    clearRange();
  };

  const onCellClick = (
    cell: CalendarCellData,
    clickedThird: CellThirdKey,
    e: React.MouseEvent,
  ) => {
    const rowsOfProperty = calendarRows.filter(
      (r) => r.propertyId === cell.idPropiedad,
    );
    const merged = mergeCellsForPropertyDay(
      cell.idPropiedad,
      cell.date,
      rowsOfProperty,
    );

    // ✅ detalle vs crear: depende del tercio clickeado, no del día completo
    const clickedThirdData = merged.thirds[clickedThird];
    const clickedThirdOccupied =
      clickedThirdData.state !== "empty" ||
      (clickedThirdData.events?.length ?? 0) > 0;

    if (clickedThirdOccupied) {
      clearRange();
      setSelectedCell(merged);
      setIsDetailOpen(true);
      return;
    }

    const clicked = {
      propertyId: merged.idPropiedad,
      date: merged.date,
      third: clickedThird,
    };

    // Primer click
    if (!rangeDraft) {
      setRangeDraft({
        propertyId: clicked.propertyId,
        propertyName: propertyNameById.get(clicked.propertyId) ?? null,
        startDate: clicked.date,
        endDate: clicked.date,
        startThird: clicked.third,
        endThird: clicked.third,
      });
      setClickAnchor(e.currentTarget.getBoundingClientRect());
      return;
    }
    // Si cambió de propiedad, reiniciar selección
    if (rangeDraft.propertyId !== clicked.propertyId) {
      setRangeDraft({
        propertyId: clicked.propertyId,
        propertyName: propertyNameById.get(clicked.propertyId) ?? null,
        startDate: clicked.date,
        endDate: clicked.date,
        startThird: clicked.third,
        endThird: clicked.third,
      });
      return;
    }

    // Segundo click igual al primero: toggle reset
    if (
      rangeDraft.startDate === clicked.date &&
      rangeDraft.endDate === clicked.date &&
      rangeDraft.startThird === clicked.third &&
      rangeDraft.endThird === clicked.third
    ) {
      clearRange();
      return;
    }

    // Completar rango
    const sIdx = dateIndexMap.get(rangeDraft.startDate);
    const eIdx = dateIndexMap.get(clicked.date);
    if (sIdx == null || eIdx == null) {
      getMsg()?.error(
        "No pude determinar el rango de fechas en la vista actual.",
      );
      clearRange();
      return;
    }

    const a = Math.min(sIdx, eIdx);
    const b = Math.max(sIdx, eIdx);
    const startDate = dates[a];
    const endDate = dates[b];

    // Si seleccionó al revés, invierte thirds también
    const forward = sIdx <= eIdx;
    const startThird = forward
      ? (rangeDraft.startThird as CellThirdKey)
      : (clicked.third as CellThirdKey);

    const endThird = forward
      ? (clicked.third as CellThirdKey)
      : (rangeDraft.endThird as CellThirdKey);

    // ✅ Validar colisiones por tercio (bitmask)
    for (let i = a; i <= b; i++) {
      const d = dates[i];
      const m = mergeCellsForPropertyDay(clicked.propertyId, d, rowsOfProperty);
      const occ = occupiedMaskOfDay(m);
      const req = requestedMaskForDay(i, a, b, startThird, endThird);
      if ((occ & req) !== 0) {
        getMsg()?.error(
          `No se puede crear: hay un evento que choca en ${d} (por tercio).`,
        );
        clearRange();
        return;
      }
    }

    const prop = properties.find((p) => p.id === clicked.propertyId);
    setRangeDraft({
      propertyId: clicked.propertyId,
      propertyName: propertyNameById.get(clicked.propertyId) ?? null,
      startDate,
      endDate,
      startThird,
      endThird,
      tasaComisionAf: prop?.tasaComisionAf ?? undefined,
      costoAseo: prop?.costoAseo ?? undefined,
      sueldoAseo: prop?.sueldoAseo ?? undefined,
    });
    setCreateMode("create");
    setEditInitialValues(null);
    openCreateModal(
      clicked.propertyId,
      startDate,
      endDate,
      { startThird, endThird },
      prop?.tasaComisionAf ?? undefined,
      prop?.costoAseo ?? undefined,
      prop?.sueldoAseo ?? undefined,
    );
  };

  const onCreate = async (payload: CreatePayload) => {
    const res = await apiFetch<{ success?: boolean; message?: string }>(
      "/stays/calendario",
      {
        method: "POST",
        body: JSON.stringify(payload),
        successMsg: createMode === "edit" ? "Guardado" : "Creado",
      },
    );
    if (!res) return;

    setIsCreateOpen(false);
    setCreateRange(null);
    setEditInitialValues(null);
    setCreateMode("create");
    clearRange();

    refetchKeepScroll();
  };

  const onEditEvent = async (ev: CalendarEventRef, slots: CellThirdKey[]) => {
    if (!selectedCell) {
      console.warn("[onEditEvent] selectedCell is null; nothing to edit");
      return;
    }

    // Rango desde el evento (lo mantenemos igual)
    const startDate =
      ev.kind === "booking"
        ? (ev.ingreso ?? selectedCell.date)
        : (dateFromISO(ev.start) ?? selectedCell.date);

    const endDate =
      ev.kind === "booking"
        ? (ev.salida ?? selectedCell.date)
        : (dateFromISO(ev.end) ?? selectedCell.date);

    const thirds = thirdFromSlots(slots);

    // id canónico
    const id =
      ev.kind === "booking"
        ? (ev.bookingDbId ?? ev.bookingId)
        : ev.kind === "cleaning"
          ? ev.cleaningId
          : ev.id;

    if (id == null) {
      getMsg()?.error("Este evento no tiene ID editable.");
      return;
    }

    const hide = getMsg()?.loading("Cargando detalle para editar…", 0);

    // Intentamos enriquecer con el detalle del backend (precios, notas
    // admin-only, etc). Si falla o devuelve null, NO abortamos: abrimos
    // igual con los datos que ya tenemos en `ev`. Antes, cualquier error
    // o respuesta vacía dejaba al usuario viendo el toast de "Cargando..."
    // sin que nada ocurriera — de ahí el reporte "el botón editar no
    // lleva a nada".
    let detail: any = null;
    try {
      detail = await fetchEventDetail(ev.kind, id);
    } catch (e: any) {
      console.error("[onEditEvent] fetchEventDetail falló:", e);
      getMsg()?.warning(
        "No pude cargar el detalle completo; abriré el editor con los datos visibles.",
      );
    }
    const d = detail ?? {};

    try {
      setCreateMode("edit");

      // Tercios canónicos para booking: SIEMPRE evening (15:00) → morning
      // (10:59), o endThird = tercio(hora_nueva) si hay salida tardía.
      //
      // Por qué NO usar `thirdFromSlots(slots)` para booking: los `slots`
      // reflejan solo los tercios ocupados en el DÍA clickeado en el
      // detail modal. Ej.: si se clickea el detail del último día del
      // booking (checkout), slots=["morning"] → initialValues.startThird y
      // endThird quedan "morning", y el check de overlap itera todos los
      // tercios del día de ingreso (mask=7 desde "morning"), detectando
      // como conflicto el arriendo consecutivo que termina en ese morning.
      // Bug reproducible: 2 arriendos consecutivos A (→29 morning) y B
      // (29 evening→30 morning); extender B reportaba falso 409 "Se
      // superpone con un arriendo el 2026-04-29 (mañana)".
      // Ver docs/calendario-cascada-arriendos.md §9.6.
      const tercioFromHHmm = (hhmm?: string): CellThirdKey => {
        if (!hhmm) return "morning";
        const [h, m] = hhmm.slice(0, 5).split(":").map(Number);
        const min = (h || 0) * 60 + (m || 0);
        if (min < 11 * 60) return "morning";
        if (min < 15 * 60) return "mid";
        return "evening";
      };
      const bookingStartThird: CellThirdKey = "evening";
      const horaNuevaBooking: string | undefined = (d as any)
        ?.adicionalesArriendo?.salida_tardia?.hora_nueva;
      const bookingEndThird: CellThirdKey = horaNuevaBooking
        ? tercioFromHHmm(horaNuevaBooking)
        : "morning";

      const initialValues: Partial<CreatePayload> = {
        id,
        kind: (ev.kind === "blocked" ? "block" : (ev.kind as any)) as any,

        // Para bloques/mantenciones los tercios se derivan de las HORAS
        // reales del backend (d.horaInicio/horaTermino), NO de `thirds`
        // (que viene de thirdFromSlots = tercios del DÍA clickeado en el
        // detalle, no del rango real del bloqueo → daba tercios erróneos y,
        // combinado con ingreso/salida vacíos, el modal abría descuadrado).
        startThird: (ev.kind === "booking"
          ? bookingStartThird
          : ev.kind === "blocked" || ev.kind === "maintenance"
            ? tercioDeHHmm(d.horaInicio ?? d.startTime)
            : thirds.startThird) as any,
        endThird: (ev.kind === "booking"
          ? bookingEndThird
          : ev.kind === "blocked" || ev.kind === "maintenance"
            ? // Mapeo de TÉRMINO (borde half-open): "11:00" = fin de mañana.
              tercioFinDeHHmm(d.horaTermino ?? d.endTime)
            : thirds.endThird) as any,

        startTime: d.startTime ?? undefined,
        endTime: d.endTime ?? undefined,

        ...(ev.kind === "booking"
          ? {
              ingreso: dayjs(d.ingreso ?? startDate),
              salida: dayjs(d.salida ?? endDate),
              nombreHuesped: d.nombreHuesped ?? ev.huesped ?? "",
              telefonoHuesped: d.telefonoHuesped ?? ev.telefono ?? "",
              cantidadHuespedes: d.cantidadHuespedes ?? 1,
              estado: d.estado ?? "booked",
              // Fallback anidado: detail (backend aplica heurística legacy)
              // → ev.precioPorNoche (buildBookingRefs también la aplica).
              // Solo caemos a 0 si ninguno de los dos tiene valor válido.
              precioNocheClp:
                (d.precioNocheClp && d.precioNocheClp > 0
                  ? d.precioNocheClp
                  : null) ??
                (ev.precioPorNoche && ev.precioPorNoche > 0
                  ? ev.precioPorNoche
                  : null) ??
                0,
              limpiezaClp: d.limpiezaClp ?? 0,
              // Campos financieros que antes se perdían al editar:
              //  - comisionPlataforma: monto (con IVA) que se quedó la plataforma.
              //  - extras: listas JSONB con {name, _f_val} (con o sin comisión AF).
              //  - descuentos: lista JSONB con {name, _f_val, tipo} — tipo es
              //    "ajuste_total" o "solo_af". El backend antes devolvía esto
              //    como un número sumado; ahora devuelve el array completo
              //    (ver normalizeDescuentosList en stays.controller.js).
              //  - estadoPago / tipoPago / notaInterna: opcionales.
              comisionPlataformaClp: d.comisionPlataformaClp ?? 0,
              extrasSinComisionClp: Array.isArray(d.extrasSinComisionClp)
                ? d.extrasSinComisionClp
                : [],
              extrasConComisionClp: Array.isArray(d.extrasConComisionClp)
                ? d.extrasConComisionClp
                : [],
              descuentoClp: Array.isArray(d.descuentoClp) ? d.descuentoClp : [],
              estadoPago: d.estadoPago ?? undefined,
              tipoPago: d.tipoPago ?? undefined,
              notaInterna: d.notaInterna ?? "",
              // Fix 2026-08-13 — origen del arriendo (ver onEditBooking).
              nombrePlataforma:
                d.nombrePlataforma ?? (ev as any).plataforma ?? undefined,
              // Round 2026-05-07: hidratamos la fecha de creación que ya
              // mostramos en el tooltip + bookingDetailModal. Sin esto el
              // DatePicker del modal de edición arrancaba vacío y al
              // guardar pisaba la fecha real con el default `ahoraChile()` del
              // create-mode. El tipo `CreatePayload.fechaCreacion` es
              // string ISO; el modal hace el cast a Dayjs internamente
              // (ver calendarCreateModal.tsx línea ~388).
              fechaCreacion: d.fechaCreacion ?? undefined,
              // Desglose de limpieza (override per-arriendo, §23). Si el
              // admin guardó un preset distinto a "default" lo hidratamos
              // en el form; si no, arranca "default" y el modal re-deriva
              // los custom inputs desde la propiedad.
              ...(d.limpiezaSubitemsOverride
                ? {
                    limpiezaPreset:
                      d.limpiezaSubitemsOverride.preset ?? "default",
                    limpiezaCustomAseador:
                      d.limpiezaSubitemsOverride.aseador ?? 0,
                    limpiezaCustomTransporteTotal:
                      d.limpiezaSubitemsOverride.transporte_total ?? 0,
                    limpiezaCustomMaterialesTotal:
                      d.limpiezaSubitemsOverride.materiales_total ?? 0,
                    limpiezaCustomLavanderiaTotal:
                      d.limpiezaSubitemsOverride.lavanderia_total ?? 0,
                  }
                : { limpiezaPreset: "default" }),
              // §28 — Cargos adicionales (extensiones / salida tardía / otros).
              // Hidratamos los 3 Form.List con lo que venga del backend. Si la
              // reserva no tiene `adicionales_arriendo`, quedan vacíos.
              extensionesNoches: Array.isArray(
                d.adicionalesArriendo?.extensiones_noches,
              )
                ? d.adicionalesArriendo.extensiones_noches
                : [],
              salidaTardia:
                d.adicionalesArriendo?.salida_tardia ?? { monto_clp: 0 },
              otrosCargos: Array.isArray(d.adicionalesArriendo?.otros)
                ? d.adicionalesArriendo.otros
                : [],
            }
          : {}),

        ...(ev.kind === "cleaning"
          ? (() => {
              // Propiedad — necesaria para derivar aseoCustom* cuando el
              // aseo NO tiene subitems_override pero stored-data diverge
              // de los defaults.
              const propForAseo = properties.find(
                (p) => p.id === selectedCell!.idPropiedad,
              );

              // Aseador breakdown en edit-mode:
              //   1. Si hay subitems_override → preset="custom", hidrato
              //      los 4 valores precisos del snapshot.
              //   2. Si no hay override pero el aseo tiene un
              //      `sueldoAseador`/`costoLimpieza` stored, hidratamos
              //      los 4 custom inputs con el aseador stored + el
              //      resto desde la propiedad. Dejamos preset="default";
              //      en edición el modal NUNCA auto-repinta (round
              //      2026-08-02: el repintado vive en el onChange del
              //      Select), así que la data hidratada acá no se pisa.
              const aseoBreakdown = d.subitemsOverride
                ? {
                    aseoCostPreset: "custom" as const,
                    aseoCustomAseador:
                      d.subitemsOverride.aseador ?? 0,
                    aseoCustomTransporteTotal:
                      d.subitemsOverride.transporte_total ?? 0,
                    aseoCustomMaterialesTotal:
                      d.subitemsOverride.materiales_total ?? 0,
                    aseoCustomLavanderiaTotal:
                      d.subitemsOverride.lavanderia_total ?? 0,
                  }
                : {
                    aseoCostPreset: "default" as const,
                    aseoCustomAseador:
                      d.sueldoAseador ??
                      propForAseo?.sueldoAseo ??
                      0,
                    aseoCustomTransporteTotal:
                      (propForAseo as any)?.transporteTotal ?? 0,
                    aseoCustomMaterialesTotal:
                      (propForAseo as any)?.materialesTotal ?? 0,
                    aseoCustomLavanderiaTotal:
                      (propForAseo as any)?.lavanderiaTotal ?? 0,
                  };

              return {
                // Hidratamos las fechas de inicio/fin del aseo en los
                // DatePickers del modal. Sin esto, el admin abría a
                // editar y los DatePickers aparecían vacíos — el
                // backend ya soporta `fecha_inicio`/`fecha_termino`
                // pero la UI no lo permitía. Preferimos el detalle
                // (`d.fecha`/`d.fechaFin` desde mapCleaningFromBackend)
                // con fallback al rango del evento (`startDate`/`endDate`).
                ingreso: dayjs(d.fecha ?? startDate),
                salida: dayjs(d.fechaFin ?? endDate),
                // Si el aseo tiene un aseador asignado, implicamos
                // modoAsignacion="designado" (sino el Select del
                // aseador queda oculto por el conditional render y el
                // admin no puede verlo ni editarlo — bug reportado).
                modoAsignacion: d.idAseador
                  ? ("designado" as const)
                  : ("abierto" as const),
                idAseador: d.idAseador ?? undefined,
                // Round 2026-08-25 — equipo 1..N con reparto (seed compartido
                // con el handler de la barra; review 2026-09-07).
                ...seedEquipoAseo(d),
                costoLimpieza:
                  d.costoLimpieza ?? (ev as any).costoLimpieza ?? 0,
                sueldoAseador:
                  d.sueldoAseador ?? (ev as any).sueldoAseador ?? 0,
                horaInicio: d.horaInicio ?? "11:00",
                urgencia: d.urgencia ?? (ev as any).urgencia ?? "am",
                estado: d.estado ?? (ev as any).estado ?? "pendiente",
                observaciones:
                  d.observaciones ?? (ev as any).notas ?? "",
                // Round 2026-05-13 — el aseo ya no lleva `rolAF` ni `pagado_a`.
                // Solo `pagado_por`. Destinatarios implícitos.
                pagado_por: d.pagado_por ?? { tipo: 0 },
                // FK al arriendo si es aseo VINCULADO/AUTO — el modal lo usa
                // para deshabilitar "Propietario/Cliente paga" (anti-doble-cobro
                // §6.4). El backend igual lo rechaza; esto es UX.
                reservaId: (ev as any).reservaId ?? (d as any).reservaId ?? null,
                // Sin detalle real, el desglose sembrado es adivinado — el
                // modal omite las claves del desglose en el payload salvo que
                // el admin toque el Modo (round 2026-08-02).
                aseoDetalleIncompleto: detail == null,
                ...aseoBreakdown,
              };
            })()
          : {}),

        ...(ev.kind === "blocked" || ev.kind === "maintenance"
          ? {
              razon: d.razon ?? (ev as any).razon ?? "",
              notas: d.notas ?? (ev as any).notas ?? "",
              // Hidratar los DatePickers con las fechas REALES del bloqueo.
              // Sin esto arrancaban vacíos → imposible editar el rango
              // (bug 2026-07-03). Preferimos el detalle del backend
              // (fechaInicio/fechaTermino) con fallback al rango del evento.
              ingreso: dayjs(d.fechaInicio ?? startDate),
              salida: dayjs(d.fechaTermino ?? endDate),
            }
          : {}),
      };

      setEditInitialValues(initialValues);

      openCreateModal(selectedCell.idPropiedad, startDate, endDate, {
        startThird: thirds.startThird as any,
        endThird: thirds.endThird as any,
      });

      setIsDetailOpen(false);
    } catch (e: any) {
      console.error("[onEditEvent] no pude abrir el modal de edición:", e);
      getMsg()?.error(e?.message ?? "No pude abrir el editor");
    } finally {
      hide?.();
    }
  };

  // Exponemos `onEditEvent` (definido en la rama admin, después del early
  // return `if (!isAdmin)`) al effect de deep-link, que vive ARRIBA junto al
  // resto de hooks — un hook no puede ir después del early return (regla de
  // hooks). El ref se actualiza en cada render admin con la última versión.
  onEditEventRef.current = onEditEvent;

  const onDeleteEvent = async (
    ev: CalendarEventRef,
    opts?: { preserveCleaning?: boolean },
  ) => {
    const id =
      ev.kind === "booking"
        ? (ev.bookingDbId ?? ev.bookingId)
        : ev.kind === "cleaning"
          ? ev.cleaningId
          : ev.id;

    if (id == null) {
      getMsg()?.error("Este evento no tiene ID eliminable.");
      return;
    }
    const kind = ev.kind === "blocked" ? "block" : ev.kind;

    const qs = new URLSearchParams({ id: String(id), kind });
    // preserveCleaning solo aplica a kind=booking. Si se pasa desde un
    // evento cleaning/block/maintenance se ignora en backend.
    if (opts?.preserveCleaning && kind === "booking") {
      qs.set("preserveCleaning", "true");
    }

    const res = await apiFetch<{
      success?: boolean;
      // Round 2026-05-29: backend ahora puede devolver `warning` cuando
      // el booking se borró OK pero algún paso del cleanup falló
      // (lavado/expense/block/aseo). Antes esos casos pasaban en silencio
      // y acumulaban filas huérfanas. Si viene warning, sobreescribimos
      // el toast success con uno amarillo para que el admin se entere.
      warning?: { code: string; message: string; failedSteps?: string[] };
    }>(`/stays/calendario?${qs.toString()}`, {
      method: "DELETE",
      successMsg:
        opts?.preserveCleaning && kind === "booking"
          ? "Arriendo eliminado · aseo conservado"
          : "Eliminado",
    });
    if (!res) return;
    if (res.warning) {
      // El success toast ya se mostró por apiFetch — añadimos uno extra
      // (warning amarillo) que el admin tenga que cerrar manualmente.
      getMsg()?.warning({
        content: res.warning.message,
        duration: 10,
      });
    }

    setIsDetailOpen(false);
    refetchKeepScroll();
  };

  async function updateEvent(payload: any) {
    const json = await apiFetch<{
      success: boolean;
      event: any;
      message?: string;
      // El backend anexa `warning` cuando la edición se guardó pero algo NO se
      // propagó (ej. subitems de un aseo cuyo arriendo tiene el mes cerrado —
      // round 2026-08-02). Mismo patrón que el DELETE con cleanup parcial.
      warning?: { code: string; message: string };
    }>("/stays/calendario", { method: "PATCH", body: JSON.stringify(payload) });
    if (!json?.success) throw new Error(json?.message ?? "Error actualizando");
    if (json.warning) {
      getMsg()?.warning({
        content: json.warning.message,
        duration: 10,
      });
    }
    setIsCreateOpen(false);
    setCreateRange(null);
    setEditInitialValues(null);
    setCreateMode("create");
    clearRange();

    refetchKeepScroll();
    return json.event;
  }

  async function deleteEvent(
    kind: string,
    id: number | string,
    opts?: { preserveCleaning?: boolean },
  ) {
    const qs = new URLSearchParams({ id: String(id), kind });
    if (opts?.preserveCleaning) qs.set("preserveCleaning", "true");

    const json = await apiFetch<{ success: boolean; message?: string }>(
      `/stays/calendario?${qs.toString()}`,
      { method: "DELETE" },
    );
    if (!json?.success) throw new Error(json?.message ?? "Error borrando");
    setIsCreateOpen(false);
    setCreateRange(null);
    setEditInitialValues(null);
    setCreateMode("create");
    clearRange();

    refetchKeepScroll();
    return true;
  }

  if (loading) {
    return (
      <div className="cal-page" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Spin size="large" tip="Cargando calendario..." />
      </div>
    );
  }

  if (error) {
    // Solo la carga INICIAL llega acá — los refreshes de fondo fallidos
    // conservan la vista y reintentan solos (ver catch de fetchCalendar).
    return (
      <div className="cal-page">
        <Alert
          type="error"
          message="Error cargando calendario"
          description={error}
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="lav-content"
    >
      <div className="cal-page">
        <div className="cal-toolbar">
          <div className="cal-title">Calendario</div>
          {/* En el celular el tip va dentro del panel «Filtros». */}
          {!isMobile && (
            <div className="cal-range-hint">
              Tip: haz 2 clicks en celdas vacías para crear un evento
            </div>
          )}
        </div>
        {/* Rango, Refrescar y el panel «Filtros», que se muestra u oculta (en el
            celular parte cerrado). Ver CalendarioControles.tsx. */}
        <CalendarioControles
          isMobile={isMobile}
          esAdmin={isAdminRole(user?.rol)}
          rangeUi={rangeUi}
          setRangeUi={setRangeUi}
          range={range}
          setRange={setRange}
          monthShortcutValue={monthShortcutValue}
          onReiniciar={() => {
            const today = ahoraChile();
            const fromD = addDays(today, -5);
            const toD = addDays(today, 15);
            setRangeUi([fromD, toD]);
            setRange({ from: fromD.format(ISO_FMT), to: toD.format(ISO_FMT) });
          }}
          refrescando={manualRefreshing}
          onRefrescar={handleRefresh}
          onCambioMasivo={() => setBulkMetaOpen(true)}
          mercadoOn={mercadoOn}
          setMercadoOn={setMercadoOn}
          mercadoPaletaId={mercadoPaletaId}
          setMercadoPaletaId={setMercadoPaletaId}
          codeSearch={codeSearch}
          setCodeSearch={setCodeSearch}
          codeSearching={codeSearching}
          onCodeSearch={handleCodeSearch}
          propSearch={propSearch}
          setPropSearch={setPropSearch}
          guestSearch={guestSearch}
          setGuestSearch={setGuestSearch}
          propSort={propSort}
          setPropSort={setPropSort}
          propFilters={propFilters}
          setPropFilters={setPropFilters}
          propFilterOptions={propFilterOptions}
        />

        {rangeDraft &&
          clickAnchor &&
          rangeDraft.startDate === rangeDraft.endDate &&
          rangeDraft.startThird === rangeDraft.endThird && (
            <div
              className="cal-tercio-popover"
              style={{
                position: "fixed",
                top: clickAnchor.bottom + 4,
                left: clickAnchor.left,
              }}
            >
              <div className="cal-tercio-popover-content">
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    const prop = properties.find(
                      (p) => p.id === rangeDraft.propertyId,
                    );
                    setCreateMode("create");
                    setEditInitialValues(null);
                    openCreateModal(
                      rangeDraft.propertyId,
                      rangeDraft.startDate,
                      rangeDraft.endDate,
                      {
                        startThird: rangeDraft.startThird as CellThirdKey,
                        endThird: rangeDraft.endThird as CellThirdKey,
                      },
                      prop?.tasaComisionAf ?? undefined,
                      prop?.costoAseo ?? undefined,
                      prop?.sueldoAseo ?? undefined,
                    );
                  }}
                >
                  Crear aquí
                </Button>
                {/*
                 * "Día completo" sólo se ofrece si TODO el día está libre.
                 * Si hay aunque sea un tercio ocupado, cubrir los 3 pisaría
                 * un evento existente — el usuario pidió ocultar la opción
                 * en ese caso para no inducir a error.
                 */}
                {!hasAnyOccupiedTercio(
                  rangeDraft.propertyId,
                  rangeDraft.startDate,
                ) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setCreateMode("create");
                      setEditInitialValues(null);
                      // "Día completo" = bloqueo que cubre las 24h del día:
                      //   - kind: "block" (admin puede cambiarlo a mantención
                      //     si quiere desde el Select; default es bloqueo).
                      //   - Tercios: morning → evening.
                      //   - Horas: 00:00 → 23:59.
                      //   - Salida = ingreso (intra-día, no cruza la noche).
                      // Esto se propaga a CalendarCreateModal vía
                      // `range.presetKind/presetStartTime/presetEndTime`, que
                      // el useEffect de apertura aplica y los useEffects de
                      // kind respetan (guardas `presetKind === kind`).
                      openCreateModal(
                        rangeDraft.propertyId,
                        rangeDraft.startDate,
                        rangeDraft.startDate,
                        { startThird: "morning", endThird: "evening" },
                        undefined,
                        undefined,
                        undefined,
                        {
                          presetKind: "block",
                          presetStartTime: "00:00",
                          presetEndTime: "23:59",
                        },
                      );
                    }}
                  >
                    Día completo
                  </Button>
                )}
                <Button size="small" type="text" onClick={clearRange}>
                  ✕
                </Button>
              </div>
            </div>
          )}

        {/*
         * Wrapper con overlay de "refreshing" — cuando una mutación
         * (create / edit / delete) dispara un refetch, mantenemos la
         * grilla anterior visible con un velo semi-transparente y un Spin
         * centrado, en vez del flash blanco anterior. Ver
         * `.cal-month-wrap` + `.cal-refresh-overlay` en calendario.css.
         */}
        <div className="cal-month-wrap">
          {refreshing && (
            <div className="cal-refresh-overlay">
              <Spin size="large" />
            </div>
          )}
          <CalendarioGrid
            calendarRows={calendarRows}
            dates={dates}
            getCell={getCell}
            todayISO={today}
            onCellClick={onCellClick}
            onCellDoubleClick={onCellDoubleClick}
            onCellContextMenu={onCellContextMenu}
            rangeSelection={rangeDraft}
            barMenuActions={barMenuActions}
            wrapperRef={wrapperRef}
            onWrapperScroll={handleWrapperScroll}
            staysCalendarData={staysCalendarData}
            fetchPropertyMeta={fetchPropertyMeta}
            canEditMeta={isAdminRole(user?.rol)}
            propertyInfoById={propertyInfoById}
            metaRefreshingIds={metaRefreshingIds}
            mercadoEnabled={mercadoOn && isAdminRole(user?.rol)}
            mercadoPaleta={mercadoPaleta}
            mercadoByProperty={mercadoByProperty}
            onMetaRangeComplete={(field, propertyId, startDate, endDate) => {
              const prop = properties.find((p) => p.id === propertyId);
              if (!prop?.idPropiedadCorto) {
                getMsg()?.warning("Esta propiedad no tiene ID de Stays configurado");
                return;
              }
              // Al re-editar una condición, precargamos la vigente de la PRIMERA
              // noche del rango para no invertir el signo (P2). Si el rango abarca
              // noches con condiciones distintas, se precarga la de la primera; el
              // admin ajusta si hace falta.
              const initialCondicion =
                field === "condicion"
                  ? (staysCalendarData[propertyId]?.condicionRaw?.[startDate] ?? null)
                  : null;
              setMetaModalProps({
                field,
                dateRange: [startDate, endDate],
                propertyId,
                propertyLabel: formatPropertyName(prop.nombre) || `Propiedad ${propertyId}`,
                initialCondicion,
              });
              setMetaModalOpen(true);
            }}
          />
        </div>

        <BookingDetailModal
          open={isDetailOpen}
          cell={selectedCell}
          propiedades={properties}
          onEditEvent={onEditEvent}
          onDeleteEvent={(ev, _slots, opts) => onDeleteEvent(ev, opts)}
          onClose={() => {
            setIsDetailOpen(false);
            setSelectedCell(null);
          }}
          userRole={user?.rol ?? null}
          onRecalculated={() => {
            // Tras recalc exitoso refrescamos el calendario completo para
            // que los nuevos valores financieros bajen al UI. Preservamos
            // el scroll porque el admin típicamente recalcula desde una
            // posición específica de la grilla y no quiere volver al top.
            refetchKeepScroll();
          }}
        />

        <MetaEditModal
          open={metaModalOpen}
          onClose={() => {
            setMetaModalOpen(false);
            setMetaModalProps(null);
          }}
          field={metaModalProps?.field ?? null}
          dateRange={metaModalProps?.dateRange ?? null}
          propertyIds={metaModalProps ? [metaModalProps.propertyId] : []}
          propertyLabel={metaModalProps?.propertyLabel ?? null}
          propertyMin={
            metaModalProps
              ? (properties.find((p) => p.id === metaModalProps.propertyId)
                  ?.precioMinimoNoche ?? null)
              : null
          }
          initialCondicion={metaModalProps?.initialCondicion ?? null}
          nightPrices={
            metaModalProps
              ? (staysCalendarData[metaModalProps.propertyId]?.efectivo ??
                staysCalendarData[metaModalProps.propertyId]?.base)
              : undefined
          }
          onSuccess={async (result) => {
            const pid = metaModalProps?.propertyId;
            if (pid == null) return;
            // Precio 0 → se crearon BLOQUEOS (inhabilitar): hay que refrescar
            // también los EVENTOS para que la barra nueva aparezca. Refresh
            // SUAVE (no colapsa filas meta ni descuadra el scroll).
            if (result?.bloqueos) {
              // Solo cambian EVENTOS (bloqueos nuevos), no precios — sin meta.
              await refreshCalendar({ includeMeta: false });
              return;
            }
            // Un cambio de meta (precio/condición/min noches/restricción) solo
            // afecta a ESTA propiedad — no toca reservas/bloqueos/aseos. Por eso
            // refrescamos únicamente su meta en su lugar, en vez de un
            // `refetchKeepScroll()` global (que resetea `staysCalendarData`,
            // colapsa las filas meta expandidas y descuadra el scroll). Ver
            // useCalendarioLogic.refreshPropertyMeta.
            // Mientras llega la re-lectura, las filas meta pulsan con spinner
            // (metaRefreshingIds) para que no se vean los valores viejos como
            // si fueran definitivos.
            markMetaRefreshing([pid], true);
            try {
              await refreshPropertyMeta(pid);
            } finally {
              markMetaRefreshing([pid], false);
            }
          }}
        />

        <MetaEditModal
          open={bulkMetaOpen}
          onClose={() => setBulkMetaOpen(false)}
          field="base"
          dateRange={bulkDateRange}
          propertyIds={[]}
          bulkProperties={bulkProperties}
          onSuccess={async () => {
            // Cambio masivo: refrescamos la meta de las propiedades abiertas
            // (las cerradas se recargan al expandirse) sin colapsar la grilla.
            // Marcamos TODAS las candidatas como "refrescando" — solo las
            // expandidas renderizan filas meta, así que el pulso se ve justo
            // donde corresponde.
            const ids = (bulkProperties ?? []).map((p) => p.id);
            markMetaRefreshing(ids, true);
            try {
              await refreshCalendar();
            } finally {
              markMetaRefreshing(ids, false);
            }
          }}
        />

        <CalendarCreateModal
          open={isCreateOpen}
          range={createRange}
          mode={createMode}
          initialValues={editInitialValues}
          onClose={() => {
            setIsCreateOpen(false);
            setCreateRange(null);
            setEditInitialValues(null);
            setCreateMode("create");
            clearRange();
          }}
          onCreate={onCreate}
          aseadores={filteredAseadoras}
          loadingAseadoras={loadingAseadoras}
          updateEvent={updateEvent}
          deleteEvent={deleteEvent}
          isDayFullyOccupied={isDayFullyOccupied}
          checkOverlap={checkAdminOverlap}
        />
      </div>
    </Layout>
  );
}
